import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PaperAirplaneIcon,
  TrashIcon,
  QuestionMarkCircleIcon,
  LightBulbIcon,
  ExclamationTriangleIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { marked } from 'marked';
import { API_BASE_URL } from '../config/api';
import { exportAPI } from '../services/api';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { useActivitiesStore } from '../store/activitiesStore';

// Helper function to generate UUIDs with fallback
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

interface HealthCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
}

interface AgeGroup {
  id: string;
  name: string;
  description: string;
}

interface AdditionalReadingLink {
  title: string;
  url: string;
  source: string;
  description?: string;
}

interface QuestionResult {
  session_id: string;
  category: string;
  question: string;
  answer: string;
  key_points: string[];
  when_to_seek_care: string[];
  related_topics: string[];
  disclaimers: string[];
  follow_up_suggestions: string[];
  additional_reading: AdditionalReadingLink[];
  timestamp: Date;
}

// Default topics when no specific category/demographics selected
const DEFAULT_TOPICS = [
  'What is a healthy blood pressure range?',
  'How much sleep do I need?',
  'What are the symptoms of vitamin D deficiency?',
  'How can I improve my heart health?',
  'What foods help lower cholesterol?',
];

// Topics by category
const TOPICS_BY_CATEGORY: Record<string, string[]> = {
  general: [
    'What is a healthy blood pressure range?',
    'How much water should I drink daily?',
    'What are signs of dehydration?',
    'How can I boost my immune system?',
    'What is a healthy BMI?',
  ],
  nutrition: [
    'What foods are high in fiber?',
    'How much protein do I need daily?',
    'What are the best sources of omega-3?',
    'What vitamins should I take?',
    'How can I reduce sugar intake?',
  ],
  fitness: [
    'How often should I exercise?',
    'What is the best exercise for weight loss?',
    'How do I build muscle safely?',
    'What are good stretches before exercise?',
    'How do I recover from a workout?',
  ],
  sleep: [
    'How much sleep do adults need?',
    'What causes insomnia?',
    'How can I improve sleep quality?',
    'What is sleep apnea?',
    'Are naps good for you?',
  ],
  mental: [
    'How can I manage stress?',
    'What are signs of anxiety?',
    'How does exercise affect mood?',
    'What is mindfulness meditation?',
    'How can I improve focus?',
  ],
  symptoms: [
    'What causes frequent headaches?',
    'Why am I always tired?',
    'What causes chest pain?',
    'What are cold vs flu symptoms?',
    'What causes digestive issues?',
  ],
  medications: [
    'What are common drug interactions?',
    'How do antibiotics work?',
    'What are blood thinner precautions?',
    'How do I manage medication side effects?',
    'When should I take vitamins?',
  ],
  preventive: [
    'What health screenings do I need?',
    'How often should I get a physical?',
    'What vaccines do adults need?',
    'How can I prevent heart disease?',
    'What are cancer warning signs?',
  ],
  chronic: [
    'How do I manage diabetes?',
    'What lifestyle changes help with hypertension?',
    'How can I manage arthritis pain?',
    'What foods are good for thyroid health?',
    'How do I monitor my asthma?',
  ],
  reproductive: [
    'What are signs of hormonal imbalance?',
    'How can I improve fertility naturally?',
    'What are menopause symptoms?',
    'What are common pregnancy concerns?',
    'How does age affect fertility?',
  ],
  aging: [
    'How can I maintain bone health?',
    'What memory changes are normal with aging?',
    'How can I stay active as I age?',
    'What health screenings do seniors need?',
    'How do I prevent falls?',
  ],
  other: DEFAULT_TOPICS,
};

// Topics by age group
const TOPICS_BY_AGE_GROUP: Record<string, string[]> = {
  child: [
    'What vaccinations do children need?',
    'How much sleep do children need?',
    'What are normal childhood development milestones?',
    'How can I help my child eat healthy?',
    'What are common childhood illnesses?',
  ],
  teen: [
    'How much sleep do teenagers need?',
    'What are signs of teen depression?',
    'How can teens manage stress?',
    'What nutrition do growing teens need?',
    'How does social media affect teen health?',
  ],
  adult: [
    'What health screenings do adults need?',
    'How can I maintain a healthy weight?',
    'What are early signs of heart disease?',
    'How much exercise do adults need?',
    'How can I manage work-related stress?',
  ],
  senior: [
    'What are signs of cognitive decline?',
    'How can I maintain bone density?',
    'What medications should seniors avoid?',
    'How can I prevent falls?',
    'What are normal aging changes vs concerns?',
  ],
};

// Topics by gender
const TOPICS_BY_GENDER: Record<string, string[]> = {
  male: [
    'What are signs of prostate issues?',
    'How can men maintain heart health?',
    'What testosterone levels are normal?',
    'How can I prevent hair loss?',
    'What screenings do men need by age?',
  ],
  female: [
    'What are signs of hormonal imbalance?',
    'How can I manage menstrual symptoms?',
    'What are breast health guidelines?',
    'What screenings do women need by age?',
    'How does menopause affect health?',
  ],
};

// Helper function to get dynamic topics based on selections
const getPopularTopics = (category: string, ageGroup: string, gender: string): string[] => {
  // Priority: gender-specific > age-group specific > category specific > default
  if (gender && TOPICS_BY_GENDER[gender]) {
    return TOPICS_BY_GENDER[gender];
  }
  if (ageGroup && TOPICS_BY_AGE_GROUP[ageGroup]) {
    return TOPICS_BY_AGE_GROUP[ageGroup];
  }
  if (category && TOPICS_BY_CATEGORY[category]) {
    return TOPICS_BY_CATEGORY[category];
  }
  return DEFAULT_TOPICS;
};

export const HealthQuestions: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  // Form state
  const [category, setCategory] = useState('general');
  const [question, setQuestion] = useState('');
  const [ageGroup, setAgeGroup] = useState('');
  const [gender, setGender] = useState('');
  const [simpleMode, setSimpleMode] = useState(true);

  // UI state
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<HealthCategory[]>([]);
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [questionHistory, setQuestionHistory] = useState<QuestionResult[]>([]);
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [initialQueryProcessed, setInitialQueryProcessed] = useState(false);

  // Activity management from store
  const { addActivity, updateActivity, currentActivityId, setCurrentActivityId, getActivityById } = useActivitiesStore();
  const [activityName, setActivityName] = useState('');
  const [lastSavedHistoryLength, setLastSavedHistoryLength] = useState(0);
  const [isSaved, setIsSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Handle initial query from navigation
  useEffect(() => {
    const state = location.state as { initialQuery?: string } | null;
    if (state?.initialQuery && !initialQueryProcessed) {
      setQuestion(state.initialQuery);
      setInitialQueryProcessed(true);
    }
  }, [location.state, initialQueryProcessed]);

  // Clear current activity and reset form when navigating without a specific activity to load
  useEffect(() => {
    const navState = location.state as { loadActivityId?: string } | null;
    if (!navState?.loadActivityId) {
      resetForm();
    }
  }, [location.pathname, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load activity if navigated from sidebar
  useEffect(() => {
    const navState = location.state as { loadActivityId?: string } | null;
    if (navState?.loadActivityId) {
      const activity = getActivityById(navState.loadActivityId);
      if (activity && activity.activityType === 'health-questions') {
        if (hasUnsavedChanges) {
          const confirmed = window.confirm(
            'You have unsaved changes. Do you want to discard them and load the selected activity?'
          );
          if (!confirmed) return;
        }

        setActivityName(activity.name);
        const savedHistory = activity.data?.questionHistory || [];
        const convertedHistory = savedHistory.map((item: any) => ({
          ...item,
          timestamp: item.timestamp ? new Date(item.timestamp) : new Date(),
        }));
        setQuestionHistory(convertedHistory);
        if (convertedHistory.length > 0) {
          setActiveSessionId(convertedHistory[convertedHistory.length - 1].session_id);
        }
        setCurrentActivityId(activity.id);
        setLastSavedHistoryLength(savedHistory.length);
        setIsSaved(true);
        setHasUnsavedChanges(false);
      }
    }
  }, [location.state, getActivityById, hasUnsavedChanges, setCurrentActivityId]);

  // Track changes for unsaved state
  useEffect(() => {
    const historyChanged = questionHistory.length !== lastSavedHistoryLength;
    if (historyChanged) {
      setHasUnsavedChanges(true);
      setIsSaved(false);
    }
  }, [questionHistory.length, lastSavedHistoryLength]);

  // Load categories and age groups on mount
  useEffect(() => {
    fetchCategories();
    fetchAgeGroups();
  }, []);

  // Scroll to top of results when new question is added
  useEffect(() => {
    if (questionHistory.length > 0) {
      resultsContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [questionHistory.length]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/health-questions/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchAgeGroups = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/health-questions/age-groups`);
      if (response.ok) {
        const data = await response.json();
        setAgeGroups(data.age_groups);
      }
    } catch (error) {
      console.error('Failed to fetch age groups:', error);
    }
  };

  const resetForm = () => {
    setCurrentActivityId(null);
    setActivityName('');
    setCategory('general');
    setQuestion('');
    setAgeGroup('');
    setGender('');
    setQuestionHistory([]);
    setActiveSessionId(null);
    setLastSavedHistoryLength(0);
    setIsSaved(false);
    setHasUnsavedChanges(false);
    setError(null);
  };

  const handleAskQuestion = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!question.trim()) {
      setError(t('healthQuestions:errors.questionRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/health-questions/ask`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          category,
          question,
          age_group: ageGroup || null,
          gender: gender || null,
          simple_mode: simpleMode,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get answer');
      }

      const data = await response.json();

      const result: QuestionResult = {
        session_id: data.session_id,
        category,
        question,
        answer: data.answer,
        key_points: data.key_points,
        when_to_seek_care: data.when_to_seek_care,
        related_topics: data.related_topics,
        disclaimers: data.disclaimers,
        follow_up_suggestions: data.follow_up_suggestions,
        additional_reading: data.additional_reading || [],
        timestamp: new Date(),
      };

      const updatedHistory = [...questionHistory, result];
      setQuestionHistory(updatedHistory);
      setActiveSessionId(data.session_id);
      setQuestion('');

      // Auto-save the activity
      let generatedName = activityName;
      if (!generatedName.trim()) {
        try {
          const token = localStorage.getItem('access_token');
          const titleResponse = await fetch(`${API_BASE_URL}/health-questions/generate-title`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { 'X-Refresh-Token': token }),
            },
            body: JSON.stringify({ question }),
          });
          if (titleResponse.ok) {
            const titleData = await titleResponse.json();
            generatedName = titleData.title || question.substring(0, 50);
          } else {
            generatedName = question.substring(0, 50);
          }
        } catch {
          generatedName = question.substring(0, 50);
        }
        setActivityName(generatedName);
      }

      const activityId = currentActivityId || generateUUID();
      if (currentActivityId) {
        updateActivity(currentActivityId, {
          name: generatedName,
          savedAt: new Date(),
          data: { questionHistory: updatedHistory },
        });
      } else {
        addActivity({
          id: activityId,
          name: generatedName,
          activityType: 'health-questions',
          savedAt: new Date(),
          data: { questionHistory: updatedHistory },
        });
        setCurrentActivityId(activityId);
      }
      setLastSavedHistoryLength(updatedHistory.length);
      setIsSaved(true);
      setHasUnsavedChanges(false);

    } catch (error) {
      console.error('Question error:', error);
      setError(t('healthQuestions:errors.askFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleFollowUp = async (sessionId: string) => {
    if (!followUpQuestion.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const lastQuestion = questionHistory.find(q => q.session_id === sessionId);

      const response = await fetch(`${API_BASE_URL}/health-questions/follow-up`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          session_id: sessionId,
          question: followUpQuestion,
          context: lastQuestion?.answer.substring(0, 2000) || '',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get follow-up response');
      }

      const data = await response.json();

      const result: QuestionResult = {
        session_id: sessionId,
        category: lastQuestion?.category || 'follow-up',
        question: `Follow-up: ${followUpQuestion}`,
        answer: data.response,
        key_points: [],
        when_to_seek_care: [],
        related_topics: [],
        disclaimers: [data.disclaimer],
        follow_up_suggestions: [],
        additional_reading: [],
        timestamp: new Date(),
      };

      const updatedHistory = [...questionHistory, result];
      setQuestionHistory(updatedHistory);
      setFollowUpQuestion('');

      // Auto-save the activity
      if (currentActivityId) {
        updateActivity(currentActivityId, {
          savedAt: new Date(),
          data: { questionHistory: updatedHistory },
        });
        setLastSavedHistoryLength(updatedHistory.length);
        setIsSaved(true);
        setHasUnsavedChanges(false);
      }

    } catch (error) {
      console.error('Follow-up error:', error);
      setError(t('healthQuestions:errors.followUpFailed'));
    } finally {
      setLoading(false);
    }
  };

  const deleteQuestion = (index: number) => {
    setQuestionHistory(questionHistory.filter((_, i) => i !== index));
  };

  // Generate export content for a question
  const generateExportContent = (result: QuestionResult): string => {
    const categoryName = categories.find(c => c.id === result.category)?.name || result.category;
    let content = `Health Question\n`;
    content += `Date: ${result.timestamp.toLocaleString()}\n`;
    content += `Category: ${categoryName}\n\n`;
    content += `### Question\n${result.question}\n\n`;
    content += `${result.answer}\n\n`;

    if (result.key_points.length > 0) {
      content += `### Key Points\n`;
      result.key_points.forEach(p => content += `- ${p}\n`);
      content += '\n';
    }

    if (result.when_to_seek_care.length > 0) {
      content += `### When to See a Healthcare Provider\n`;
      result.when_to_seek_care.forEach(w => content += `- ${w}\n`);
      content += '\n';
    }

    content += `### Disclaimer\n`;
    result.disclaimers.forEach(d => content += `${d}\n`);

    return content;
  };

  // Handle export for a specific result
  const handleExportForResult = (index: number) => async (format: 'word' | 'pdf' | 'text') => {
    const result = questionHistory[index];
    if (!result) return;
    await exportDocument(result, format);
  };

  // Handle print for a specific result
  const handlePrintForResult = (index: number) => () => {
    const result = questionHistory[index];
    if (!result) return;
    handlePrint(result);
  };

  // Handle print
  const handlePrint = (result: QuestionResult) => {
    const categoryName = categories.find(c => c.id === result.category)?.name || result.category;
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${t('healthQuestions:title', 'Health Question')}</title>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            max-width: 800px;
            margin: 0 auto;
            padding: 40px 20px;
            color: #333;
          }
          h1 { text-align: center; color: #2563eb; border-bottom: 2px solid #2563eb; padding-bottom: 10px; }
          h2 { color: #1d4ed8; margin-top: 24px; }
          .meta { text-align: center; color: #666; margin-bottom: 24px; }
          ul { margin: 0; padding-left: 20px; }
          li { margin-bottom: 8px; }
          .disclaimer { margin-top: 24px; padding: 12px; background: #fef3c7; border-radius: 8px; font-size: 12px; }
          .warning { margin-top: 16px; padding: 12px; background: #fee2e2; border-radius: 8px; }
          .warning h3 { color: #dc2626; margin: 0 0 8px 0; font-size: 14px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>${t('healthQuestions:title', 'Health Question')}</h1>
        <div class="meta">
          <p>${t('healthQuestions:date', 'Date')}: ${result.timestamp.toLocaleString()}</p>
          <p>${t('healthQuestions:category', 'Category')}: ${categoryName}</p>
        </div>
        <h2>${t('healthQuestions:yourQuestion', 'Your Question')}</h2>
        <p>${result.question}</p>
        <div class="answer-content">${marked(result.answer, { async: false }) as string}</div>
        ${
          result.key_points.length > 0
            ? `<h2>${t('healthQuestions:keyPoints', 'Key Points')}</h2>
          <ul>${result.key_points.map((p) => `<li>${p}</li>`).join('')}</ul>`
            : ''
        }
        ${
          result.when_to_seek_care.length > 0
            ? `<div class="warning"><h3>${t('healthQuestions:whenToSeekCare', 'When to See a Healthcare Provider')}</h3>
          <ul>${result.when_to_seek_care.map((w) => `<li>${w}</li>`).join('')}</ul></div>`
            : ''
        }
        <div class="disclaimer">
          <strong>${t('healthQuestions:disclaimer.title', 'Disclaimer')}:</strong> ${result.disclaimers[0]}
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  // Export document to Word, PDF, or Text
  const exportDocument = async (result: QuestionResult, format: 'word' | 'pdf' | 'text') => {
    try {
      const content = generateExportContent(result);
      const title = t('healthQuestions:title', 'Health Question');
      const extensionMap: Record<string, string> = { word: 'docx', pdf: 'pdf', text: 'txt' };
      const extension = extensionMap[format];
      const filename = `health-question-${result.session_id.substring(0, 8)}.${extension}`;

      const blob = await exportAPI.exportDocument({
        content,
        title,
        format: extension as 'docx' | 'pdf' | 'txt',
      });

      // Try File System Access API
      if ('showSaveFilePicker' in window) {
        try {
          const mimeTypes: Record<string, string> = {
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            pdf: 'application/pdf',
            txt: 'text/plain',
          };

          const descriptions: Record<string, string> = {
            word: 'Word Document',
            pdf: 'PDF Document',
            text: 'Text File',
          };

          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [
              {
                description: descriptions[format],
                accept: { [mimeTypes[extension]]: [`.${extension}`] },
              },
            ],
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch (err: any) {
          if (err.name === 'AbortError') return;
        }
      }

      // Fallback download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      setError(t('healthQuestions:errors.exportFailed', 'Failed to export document'));
    }
  };

  return (
    <div className="flex flex-col h-full p-6">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center mb-4">
          <QuestionMarkCircleIcon className="h-12 w-12 text-blue-500" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {t('healthQuestions:title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('healthQuestions:subtitle')}
        </p>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row gap-6 max-w-7xl mx-auto w-full overflow-hidden">
        {/* Left Panel - Options */}
        <div className="lg:w-1/4 flex flex-col space-y-4 overflow-y-auto">
          {/* Category Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('healthQuestions:form.category')}
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
            {category && categories.find(c => c.id === category) && (
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                {categories.find(c => c.id === category)?.description}
              </p>
            )}
          </div>

          {/* Age Group */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('healthQuestions:form.ageGroup')}
            </label>
            <select
              value={ageGroup}
              onChange={(e) => setAgeGroup(e.target.value)}
              className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('healthQuestions:form.selectAgeGroup')}</option>
              {ageGroups.map((ag) => (
                <option key={ag.id} value={ag.id}>
                  {ag.name}
                </option>
              ))}
            </select>
          </div>

          {/* Gender */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('healthQuestions:form.gender')}
            </label>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('healthQuestions:form.selectGender')}</option>
              <option value="male">{t('healthQuestions:form.male')}</option>
              <option value="female">{t('healthQuestions:form.female')}</option>
              <option value="other">{t('healthQuestions:form.other')}</option>
            </select>
          </div>

          {/* Simple Mode Toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('healthQuestions:form.simpleMode')}
              </span>
              <p className="text-xs text-gray-600 dark:text-gray-300">
                {t('healthQuestions:form.simpleModeDesc')}
              </p>
            </div>
            <button
              onClick={() => setSimpleMode(!simpleMode)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                simpleMode ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  simpleMode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Popular Topics - Dynamic based on selections */}
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
              {t('healthQuestions:popularTopics', 'Popular Topics')}
              {(gender || ageGroup || category !== 'general') && (
                <span className="font-normal text-xs text-blue-600 dark:text-blue-400 ml-1">
                  ({gender ? (gender === 'male' ? 'Men' : gender === 'female' ? 'Women' : 'Other') :
                    ageGroup ? ageGroups.find(ag => ag.id === ageGroup)?.name || ageGroup :
                    categories.find(c => c.id === category)?.name || category})
                </span>
              )}
            </h3>
            <div className="space-y-1">
              {getPopularTopics(category, ageGroup, gender).map((topic, i) => (
                <button
                  key={i}
                  onClick={() => setQuestion(topic)}
                  className="block w-full text-left text-sm text-blue-700 dark:text-blue-300 hover:underline"
                >
                  {topic}
                </button>
              ))}
            </div>
          </div>

          {/* Reset Button */}
          <button
            onClick={resetForm}
            className="w-full px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            {t('healthQuestions:reset')}
          </button>
        </div>

        {/* Right Panel - Question & Answers */}
        <div className="lg:w-3/4 flex flex-col bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden">
          {/* Question Input */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <form onSubmit={handleAskQuestion}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('healthQuestions:form.question')}
              </label>
              <div className="relative">
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder={t('healthQuestions:form.questionPlaceholder')}
                  rows={3}
                  className="w-full px-4 py-3 pr-12 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading || !question.trim()}
                  className="absolute bottom-3 right-3 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  title={t('healthQuestions:form.ask')}
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                </button>
              </div>
              {error && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
              )}
            </form>
          </div>

          {/* Question History */}
          <div ref={resultsContainerRef} className="flex-1 overflow-y-auto p-4 space-y-6">
            {questionHistory.length === 0 ? (
              <div className="text-center py-12 text-gray-600 dark:text-gray-300">
                <LightBulbIcon className="h-16 w-16 mx-auto mb-4 opacity-60" />
                <p>{t('healthQuestions:noQuestions')}</p>
                <p className="text-sm mt-2 text-gray-500 dark:text-gray-400">{t('healthQuestions:getStarted')}</p>
              </div>
            ) : (
              [...questionHistory].reverse().map((result, reversedIndex) => {
                // Calculate the actual index in the original array for operations
                const actualIndex = questionHistory.length - 1 - reversedIndex;
                return (
                <div
                  key={`${result.session_id}-${actualIndex}`}
                  className="bg-white dark:bg-gray-700 rounded-lg shadow p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100 rounded">
                        {categories.find(c => c.id === result.category)?.name || result.category}
                      </span>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                        {result.timestamp.toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <ExportDropdownMenu
                        onExport={handleExportForResult(actualIndex)}
                        onPrint={handlePrintForResult(actualIndex)}
                        showPrint={true}
                      />
                      <button
                        onClick={() => deleteQuestion(actualIndex)}
                        className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                        title={t('healthQuestions:delete', 'Delete')}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      {t('healthQuestions:yourQuestion')}:
                    </h4>
                    <p className="text-gray-700 dark:text-gray-200 text-sm italic">
                      {result.question}
                    </p>
                  </div>

                  <div className="prose dark:prose-invert max-w-none text-gray-900 dark:text-gray-100 prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-p:text-gray-800 dark:prose-p:text-gray-200 prose-li:text-gray-800 dark:prose-li:text-gray-200 prose-strong:text-gray-900 dark:prose-strong:text-white">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {result.answer}
                    </ReactMarkdown>
                  </div>

                  {result.key_points.length > 0 && (
                    <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
                        {t('healthQuestions:keyPoints')}
                      </h4>
                      <ul className="list-disc list-inside space-y-1">
                        {result.key_points.map((point, i) => (
                          <li key={i} className="text-sm text-green-700 dark:text-green-300">
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.when_to_seek_care.length > 0 && (
                    <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <h4 className="font-medium text-red-800 dark:text-red-200 mb-2 flex items-center">
                        <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                        {t('healthQuestions:whenToSeekCare')}
                      </h4>
                      <ul className="list-disc list-inside space-y-1">
                        {result.when_to_seek_care.map((item, i) => (
                          <li key={i} className="text-sm text-red-700 dark:text-red-300">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.follow_up_suggestions.length > 0 && (
                    <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                        {t('healthQuestions:suggestedFollowUps')}
                      </h4>
                      <ul className="space-y-1">
                        {result.follow_up_suggestions.map((suggestion, i) => (
                          <li
                            key={i}
                            className="text-sm text-blue-700 dark:text-blue-300 cursor-pointer hover:underline"
                            onClick={() => setFollowUpQuestion(suggestion)}
                          >
                            {suggestion}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.additional_reading && result.additional_reading.length > 0 && (
                    <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <h4 className="font-medium text-purple-800 dark:text-purple-200 mb-2 flex items-center">
                        <LinkIcon className="h-5 w-5 mr-2" />
                        {t('healthQuestions:additionalReading')}
                      </h4>
                      <ul className="space-y-2">
                        {result.additional_reading.map((link, i) => (
                          <li key={i} className="text-sm">
                            <a
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-purple-700 dark:text-purple-300 hover:underline font-medium"
                            >
                              {link.title}
                            </a>
                            <span className="text-purple-600 dark:text-purple-400 ml-2">
                              — {link.source}
                            </span>
                            {link.description && (
                              <p className="text-purple-600 dark:text-purple-400 text-xs mt-0.5">
                                {link.description}
                              </p>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
                    <p className="text-xs text-amber-800 dark:text-amber-200">
                      {result.disclaimers[0]}
                    </p>
                  </div>
                </div>
              );})
            )}
          </div>

          {/* Follow-up Input */}
          {activeSessionId && questionHistory.length > 0 && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={followUpQuestion}
                  onChange={(e) => setFollowUpQuestion(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleFollowUp(activeSessionId)}
                  placeholder={t('healthQuestions:form.followUpPlaceholder')}
                  className="flex-1 px-4 py-2 bg-gray-50 dark:bg-gray-600 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                />
                <button
                  onClick={() => handleFollowUp(activeSessionId)}
                  disabled={loading || !followUpQuestion.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? '...' : t('healthQuestions:form.askFollowUp')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Disclaimer Footer */}
      <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg max-w-7xl mx-auto w-full">
        <p className="text-sm text-amber-800 dark:text-amber-200 text-center">
          <strong>{t('healthQuestions:disclaimer.title')}:</strong> {t('healthQuestions:disclaimer.text')}
        </p>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 flex items-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="text-gray-900 dark:text-gray-100">{t('healthQuestions:processing')}</span>
          </div>
        </div>
      )}

    </div>
  );
};
